import React, { useEffect, useState } from 'react';
import supabase from '../config/supabaseClient';
import '../components/eventchat.css';

// Timestamp for messages - +5min
const formatTimestamp = (ms) => {
  const date = new Date(ms);
  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
};

export default function EventChat({ eventId }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [userId, setUserId] = useState('');
  const [userProfiles, setUserProfiles] = useState({});


  // 🔄 Fetch user profile from Supabase (like in settings.js)
  useEffect(() => {
    const fetchProfile = async () => {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (profile) {
          setFirstName(profile.first_name);
          setLastName(profile.last_name);
          setAvatarUrl(profile.user_avatar || '');
        }

        if (profileError) console.error('Error loading profile:', profileError.message);
      }
      if (error) console.error('Auth error:', error.message);
    };

    fetchProfile();
  }, []);

  // 💬 Fetch chat thread for this event
  useEffect(() => {
    const fetchChat = async () => {
      const { data, error } = await supabase
        .from('hosting')
        .select('event_chat')
        .eq('id', eventId)
        .single();

      if (data && data.event_chat) {
        try {
          const parsed = JSON.parse(data.event_chat);
          setMessages(parsed);
        } catch (err) {
          console.error('Failed to parse event_chat JSON', err);
        }
      }

      if (error) console.error('Error fetching chat:', error);
    };

    fetchChat();
  }, [eventId]);

    useEffect(() => {
      const fetchProfilesForMessages = async () => {
        const uniqueUserIds = [...new Set(messages.map(m => m.user_id))].filter(Boolean);
        if (uniqueUserIds.length === 0) return;

        const { data, error } = await supabase
          .from('profiles')
          .select('user_id, first_name, last_name, user_avatar, user_photo, avatar_or_pic, is_private')
          .in('user_id', uniqueUserIds);

        if (error) {
          console.error('Error fetching profiles:', error);
          return;
        }

        const profileMap = {};
        data.forEach((profile) => {
          profileMap[profile.user_id] = profile;
        });

        setUserProfiles(profileMap);
      };

      fetchProfilesForMessages();
    }, [messages]);

  // 🔽 Scroll to latest message
  useEffect(() => {
    const chatContainer = document.querySelector('.chat-messages');
    if (chatContainer) {
      chatContainer.scrollTop = chatContainer.scrollHeight;
    }
  }, [messages]);

  // ⌨️ Send message on Enter
  const handleKeyDown = async (e) => {
    if (e.key === 'Enter' && newMessage.trim()) {
      const displayName = `${firstName} ${lastName?.[0] || ''}`.trim();
      let avatar;

    if (!avatarUrl || avatarUrl === '' || avatarUrl === null) {
      avatar = `https://api.dicebear.com/7.x/bottts/svg?seed=${firstName || 'user'}`;
    } else {
      avatar = avatarUrl;
    }


    const newEntry = {
      user_id: userId,
      text: newMessage.trim(),
      timestamp: Date.now(),
    };

      const updatedMessages = [...messages, newEntry];
      setMessages(updatedMessages);
      setNewMessage('');

      const { error } = await supabase
        .from('hosting')
        .update({ event_chat: JSON.stringify(updatedMessages) })
        .eq('id', eventId);

      if (error) console.error('Failed to update chat:', error);
    }
  };

      return (
        <div className="chat-box-container">
          <div className="chat-messages">
            {messages.map((msg, index) => {
      const prev = messages[index - 1];
      const isSameUser = prev?.user_id === msg.user_id;
      const isFirstMessage = index === 0;
      const toMs = (t) => (typeof t === 'number' ? t : isNaN(Number(t)) ? Date.parse(t) : Number(t));
      const prevTimestamp = toMs(prev?.timestamp);
      const currentTimestamp = toMs(msg.timestamp);
      const timeGap = currentTimestamp - prevTimestamp;
      const showTimestamp = isFirstMessage || timeGap > 5 * 60 * 1000; // 5 minutes
      const profile = userProfiles[msg.user_id];
      const name = profile
        ? `${profile.first_name || ''}${profile.last_name?.[0] || ''}`
        : 'Guest';

      let avatar;

      if (profile?.is_private) {
        avatar = `https://api.dicebear.com/7.x/bottts/svg?seed=${profile?.first_name || 'user'}`;
      } else if (profile?.avatar_or_pic === 'photo' && profile.user_photo) {
        avatar = profile.user_photo;
      } else if (profile?.user_avatar) {
        avatar = profile.user_avatar;
      } else {
        avatar = `https://api.dicebear.com/7.x/bottts/svg?seed=${profile?.first_name || 'user'}`;
      }

    return (
    <React.Fragment key={index}>

      <div className={`chat-message ${isSameUser && !showTimestamp ? 'continuation' : ''}`}>
        {(!isSameUser || showTimestamp) ? (
          <>
            <img src={avatar} alt="avatar" className="chat-avatar" />
            <div className="chat-bubble">
              <div className="chat-header-row">
                <span className="chat-name">{name}</span>
                {showTimestamp && (
                  <span className="chat-inline-timestamp">
                    {formatTimestamp(currentTimestamp)}
                  </span>
                )}
              </div>
              <div className="chat-text">{msg.text}</div>
            </div>
          </>
        ) : (
          <div className="chat-text">{msg.text}</div>
        )}
      </div>
    </React.Fragment>

    );
    })}

      </div>

      <input
        type="text"
        className="chat-input"
        placeholder="Chat here..."
        value={newMessage}
        onChange={(e) => setNewMessage(e.target.value)}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
}
